import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import ExtensionIcon from '@mui/icons-material/Extension';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import WaehrungField from './WaehrungField';

import ConfirmDialog from '../common/ConfirmDialog';
import TableSkeleton from '../common/TableSkeleton';
import { useNotification } from '../../hooks/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import eingangsbelegeService, {
  AnbieterKonto,
  InvoiceOwner,
  ProviderDefinition,
} from '../../services/eingangsbelegeService';
import ConnectorEinrichtenDialog from './ConnectorEinrichtenDialog';

interface Props {
  providers: ProviderDefinition[];
  canManage: boolean;
  refreshKey: number;
  onChanged: () => void;
}

const statusConfig: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  nie: { label: 'Nicht verbunden', color: 'default' },
  ok: { label: 'Synchronisiert', color: 'success' },
  login_noetig: { label: 'Login erforderlich', color: 'warning' },
  fehler: { label: 'Fehler', color: 'error' },
};

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);
const isZipFile = (file: File) =>
  file.name.toLowerCase().endsWith('.zip')
  || file.type === 'application/zip'
  || file.type === 'application/x-zip-compressed';

const UPLOAD_HINT = 'Eine einzelne PDF-Rechnung oder ein Cursor-ZIP (cursor-invoices-….zip inkl. manifest.csv) ablegen. Ausgefüllte Felder haben Vorrang vor der automatischen Erkennung. Leere Felder werden aus der Datei oder der CSV übernommen. Dieselbe Rechnung wird beim erneuten Upload erkannt.';

const AnbieterKontenTab: React.FC<Props> = ({
  providers,
  canManage,
  refreshKey,
  onChanged,
}) => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<AnbieterKonto[]>([]);
  const [owners, setOwners] = useState<InvoiceOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AnbieterKonto | null>(null);
  const [uploadAccount, setUploadAccount] = useState<AnbieterKonto | null>(null);
  const [connectorAccount, setConnectorAccount] = useState<AnbieterKonto | null>(null);
  const [deactivateAccount, setDeactivateAccount] = useState<AnbieterKonto | null>(null);
  const [saving, setSaving] = useState(false);

  const [providerCode, setProviderCode] = useState('cursor');
  const [providerName, setProviderName] = useState('');
  const [label, setLabel] = useState('');
  const [email, setEmail] = useState('');
  const [ownerId, setOwnerId] = useState<number | ''>('');
  const [interval, setInterval] = useState('monatlich');
  const [activeFrom, setActiveFrom] = useState(today());
  const [expectedDay, setExpectedDay] = useState('7');
  const [syncMethod, setSyncMethod] = useState('browser_connector');

  const [files, setFiles] = useState<File[]>([]);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(currentMonth());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [grossAmount, setGrossAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [accountData, ownerData] = await Promise.all([
        eingangsbelegeService.accounts(),
        canManage ? eingangsbelegeService.owners() : Promise.resolve([]),
      ]);
      setAccounts(accountData);
      setOwners(ownerData);
    } catch {
      // Axios-Interceptor zeigt den Fehler.
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const visibleAccounts = useMemo(
    () => accounts.filter((account) => showInactive || account.aktiv),
    [accounts, showInactive],
  );

  const selectedProvider = providers.find((provider) => provider.code === providerCode);
  const connectorProvider = providers.find((provider) => provider.code === connectorAccount?.anbieter_code);

  const resetForm = (account?: AnbieterKonto) => {
    setEditing(account || null);
    setProviderCode(account?.anbieter_code || 'cursor');
    setProviderName(account?.anbieter_name || '');
    setLabel(account?.bezeichnung || '');
    setEmail(account?.konto_email || '');
    setOwnerId(account?.owner_id || user?.id || '');
    setInterval(account?.abrechnungsintervall || 'monatlich');
    setActiveFrom(account?.aktiv_ab || today());
    setExpectedDay(String(account?.erwartet_bis_tag || 7));
    setSyncMethod(account?.sync_methode || (account?.anbieter_code === 'sonstige' ? 'manuell' : 'browser_connector'));
    setFormOpen(true);
  };

  const handleProviderChange = (code: string) => {
    setProviderCode(code);
    const provider = providers.find((item) => item.code === code);
    setSyncMethod(provider?.browser_connector ? 'browser_connector' : 'manuell');
  };

  const saveAccount = async () => {
    try {
      setSaving(true);
      const payload = {
        anbieter_code: providerCode,
        anbieter_name: providerCode === 'sonstige' ? providerName : null,
        bezeichnung: label,
        konto_email: email || null,
        ...(canManage ? { owner_id: ownerId === '' ? null : Number(ownerId) } : {}),
        abrechnungsintervall: interval,
        aktiv_ab: activeFrom,
        erwartet_bis_tag: interval === 'monatlich' ? Number(expectedDay) : null,
        sync_methode: syncMethod,
      };
      if (editing) {
        const { anbieter_code: _provider, ...updatePayload } = payload;
        await eingangsbelegeService.updateAccount(editing.id, updatePayload);
        showSuccess('Anbieter-Konto aktualisiert');
      } else {
        await eingangsbelegeService.createAccount(payload);
        showSuccess('Anbieter-Konto angelegt');
      }
      setFormOpen(false);
      await load();
      onChanged();
    } catch {
      // Axios-Interceptor zeigt Details.
    } finally {
      setSaving(false);
    }
  };

  const selectUploadFiles = useCallback((list: FileList | readonly File[]) => {
    const next = Array.from(list);
    if (next.length === 0) return;
    if (next.length > 1) {
      showError('Bitte nur eine einzelne Rechnung oder ein ZIP ablegen.');
      return;
    }
    setFiles(next);
  }, [showError]);

  const uploadDragInside = (event: React.DragEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return (
      event.clientX >= rect.left
      && event.clientX <= rect.right
      && event.clientY >= rect.top
      && event.clientY <= rect.bottom
    );
  };

  const handleUploadDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (saving) return;
    const types = Array.from(event.dataTransfer?.types || []);
    if (types.length === 0 || types.includes('Files')) {
      setUploadDragOver(true);
    }
  };

  const handleUploadDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = saving ? 'none' : 'copy';
    }
    if (!saving) {
      setUploadDragOver(true);
    }
  };

  const handleUploadDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    if (uploadDragInside(event)) return;
    setUploadDragOver(false);
  };

  const handleUploadDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setUploadDragOver(false);
    if (saving) return;
    selectUploadFiles(event.dataTransfer.files);
  };

  const openUpload = (account: AnbieterKonto) => {
    setUploadAccount(account);
    setFiles([]);
    setUploadDragOver(false);
    setUploadMonth(currentMonth());
    setInvoiceNumber('');
    setGrossAmount('');
    setCurrency('EUR');
  };

  const closeUpload = () => {
    setUploadAccount(null);
    setUploadDragOver(false);
  };

  const upload = async () => {
    if (!uploadAccount || files.length !== 1) return;
    const zip = isZipFile(files[0]);
    try {
      setSaving(true);
      const result = await eingangsbelegeService.upload(uploadAccount.id, files, {
        leistungsmonat: zip ? null : (uploadMonth || null),
        rechnungsnummer: zip ? null : (invoiceNumber || null),
        bruttobetrag: zip || !grossAmount ? null : Number(grossAmount.replace(',', '.')),
        waehrung: zip ? null : (currency || null),
      });
      const imported = result.filter((item) => !item.duplicate).length;
      const duplicates = result.length - imported;
      showSuccess(`${imported} Beleg(e) importiert${duplicates ? `, ${duplicates} Duplikat(e)` : ''}`);
      setUploadAccount(null);
      await load();
      onChanged();
    } catch {
      // Axios-Interceptor zeigt Details.
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!deactivateAccount) return;
    try {
      await eingangsbelegeService.deactivateAccount(deactivateAccount.id);
      showSuccess('Anbieter-Konto deaktiviert und Connectoren widerrufen');
      setDeactivateAccount(null);
      await load();
      onChanged();
    } catch {
      showError('Konto konnte nicht deaktiviert werden');
    }
  };

  if (loading) return <TableSkeleton rows={5} columns={6} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">Anbieter-Konten</Typography>
          <Typography variant="body2" color="text.secondary">
            Jedes Konto gehört einem Mitarbeiter und bleibt für die Buchhaltung nachvollziehbar.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />}
            label="Inaktive"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => resetForm()}>
            Anbieter-Konto
          </Button>
        </Box>
      </Box>

      {visibleAccounts.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Noch kein Anbieter-Konto</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Legen Sie Ihr Cursor- oder ein anderes SaaS-Konto an, um Rechnungen bereitzustellen.
          </Typography>
          <Button variant="contained" onClick={() => resetForm()}>Erstes Konto anlegen</Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Anbieter / Konto</TableCell>
                {canManage && <TableCell>Eigentümer</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Letzter Abruf</TableCell>
                <TableCell align="center">Belege</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleAccounts.map((account) => {
                const status = statusConfig[account.sync_status] || statusConfig.nie;
                const provider = providers.find((item) => item.code === account.anbieter_code);
                return (
                  <TableRow key={account.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {provider?.label || account.anbieter_name || account.anbieter_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.bezeichnung}{account.konto_email ? ` · ${account.konto_email}` : ''}
                      </Typography>
                      {!account.aktiv && <Chip size="small" label="Inaktiv" sx={{ ml: 1 }} />}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        {account.owner
                          ? `${account.owner.vorname} ${account.owner.nachname}`
                          : <Chip size="small" color="warning" label="Nicht zugeordnet" />}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip size="small" label={status.label} color={status.color} />
                      {account.aktive_connectoren > 0 && (
                        <Chip size="small" variant="outlined" label={`${account.aktive_connectoren} Browser`} sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {account.letzter_sync_am
                        ? new Date(account.letzter_sync_am).toLocaleString('de-DE')
                        : '–'}
                      {account.letzter_fehler_hinweis && (
                        <Typography variant="caption" color="error" display="block">
                          {account.letzter_fehler_hinweis}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{account.belege_anzahl}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="PDF manuell hochladen">
                        <IconButton aria-label="PDF manuell hochladen" onClick={() => openUpload(account)} disabled={!account.aktiv}>
                          <CloudUploadIcon />
                        </IconButton>
                      </Tooltip>
                      {account.anbieter_code === 'cursor' && (
                        <Tooltip title="Browser-Connector einrichten">
                          <IconButton aria-label="Browser-Connector einrichten" onClick={() => setConnectorAccount(account)} disabled={!account.aktiv}>
                            <ExtensionIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Bearbeiten">
                        <IconButton aria-label="Anbieter-Konto bearbeiten" onClick={() => resetForm(account)}><EditIcon /></IconButton>
                      </Tooltip>
                      {account.aktiv && (
                        <Tooltip title="Deaktivieren">
                          <IconButton aria-label="Anbieter-Konto deaktivieren" color="error" onClick={() => setDeactivateAccount(account)}>
                            <LinkOffIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Anbieter-Konto bearbeiten' : 'Anbieter-Konto anlegen'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            select
            label="Anbieter"
            value={providerCode}
            disabled={!!editing}
            onChange={(event) => handleProviderChange(event.target.value)}
          >
            {providers.map((provider) => (
              <MenuItem key={provider.code} value={provider.code}>{provider.label}</MenuItem>
            ))}
          </TextField>
          {providerCode === 'sonstige' && (
            <TextField label="Anbietername" value={providerName} onChange={(event) => setProviderName(event.target.value)} />
          )}
          <TextField required label="Bezeichnung" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="z. B. Cursor – Max Mustermann" />
          <TextField label="Konto-E-Mail" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          {canManage && (
            <TextField select label="Eigentümer" value={ownerId} onChange={(event) => setOwnerId(event.target.value === '' ? '' : Number(event.target.value))}>
              <MenuItem value=""><em>Nicht zugeordnet</em></MenuItem>
              {owners.map((owner) => (
                <MenuItem key={owner.id} value={owner.id}>
                  {owner.vorname} {owner.nachname} ({owner.email})
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField select label="Abrechnungsintervall" value={interval} onChange={(event) => setInterval(event.target.value)}>
            <MenuItem value="monatlich">Monatlich</MenuItem>
            <MenuItem value="jaehrlich">Jährlich</MenuItem>
            <MenuItem value="unregelmaessig">Unregelmäßig</MenuItem>
          </TextField>
          <TextField label="Aktiv ab" type="date" value={activeFrom} onChange={(event) => setActiveFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
          {interval === 'monatlich' && (
            <TextField label="Erwartet bis Tag des Folgemonats" type="number" value={expectedDay} onChange={(event) => setExpectedDay(event.target.value)} inputProps={{ min: 1, max: 28 }} />
          )}
          <TextField select label="Bereitstellung" value={syncMethod} onChange={(event) => setSyncMethod(event.target.value)}>
            {(selectedProvider?.sync_methoden || ['manuell']).map((method) => (
              <MenuItem key={method} value={method}>
                {method === 'browser_connector' ? 'Browser-Connector' : 'Manueller PDF-Upload'}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={saveAccount} disabled={saving || !label.trim() || (providerCode === 'sonstige' && !providerName.trim())}>
            {saving ? 'Speichert…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!uploadAccount}
        onClose={closeUpload}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          onDragEnter: handleUploadDragEnter,
          onDragOver: handleUploadDragOver,
          onDragLeave: handleUploadDragLeave,
          onDrop: handleUploadDrop,
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Rechnung hochladen
          <Tooltip title={UPLOAD_HINT}>
            <IconButton size="small" aria-label="Hinweis zum Hochladen" sx={{ color: 'text.secondary' }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box
            data-testid="rechnungen-upload-dropzone"
            data-drag-over={uploadDragOver ? 'true' : 'false'}
            onDragEnter={handleUploadDragEnter}
            onDragOver={handleUploadDragOver}
            onDragLeave={handleUploadDragLeave}
            onDrop={handleUploadDrop}
            sx={{
              border: '2px dashed',
              borderColor: uploadDragOver ? 'primary.main' : 'divider',
              borderRadius: 2,
              px: 2,
              py: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              textAlign: 'center',
              bgcolor: uploadDragOver ? 'action.hover' : 'transparent',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
            }}
          >
            <CloudUploadIcon color={uploadDragOver ? 'primary' : 'action'} sx={{ fontSize: 36 }} />
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={saving}>
              PDF oder Cursor-ZIP auswählen
              <input
                hidden
                type="file"
                accept="application/pdf,.pdf,application/zip,.zip"
                onChange={(event) => {
                  selectUploadFiles(event.target.files || []);
                  event.target.value = '';
                }}
              />
            </Button>
            {uploadDragOver && (
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                PDF oder Cursor-ZIP hier ablegen
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {files.length ? files[0].name : 'Keine Datei ausgewählt'}
            </Typography>
          </Box>
          {files[0] && isZipFile(files[0]) ? (
            <Typography variant="body2" color="text.secondary">
              Nummer, Betrag, Währung und Monat werden aus dem ZIP übernommen.
            </Typography>
          ) : (
            <>
              <TextField label="Leistungsmonat" type="month" value={uploadMonth} onChange={(event) => setUploadMonth(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Rechnungsnummer (optional)" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Bruttobetrag (optional)" value={grossAmount} onChange={(event) => setGrossAmount(event.target.value)} />
                <WaehrungField value={currency} onChange={setCurrency} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUpload}>Abbrechen</Button>
          <Button variant="contained" onClick={upload} disabled={saving || files.length === 0}>
            {saving ? 'Lädt hoch…' : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConnectorEinrichtenDialog
        open={!!connectorAccount}
        account={connectorAccount}
        provider={connectorProvider}
        onClose={() => setConnectorAccount(null)}
        onChanged={() => {
          load();
          onChanged();
        }}
      />

      <ConfirmDialog
        open={!!deactivateAccount}
        title="Anbieter-Konto deaktivieren"
        message={`Das Konto „${deactivateAccount?.bezeichnung}“ wird deaktiviert und alle Browser-Zugriffe werden widerrufen. Vorhandene Belege bleiben erhalten.`}
        onConfirm={deactivate}
        onCancel={() => setDeactivateAccount(null)}
      />
    </Box>
  );
};

export default AnbieterKontenTab;
